import { Fragment, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getChartOfAccounts, getChartOfAccountEntries } from '../../services/LedgerService'
import { ROLE_SLUGS } from '../../utils/auth/permissionConstants'
import { scrollToPageTop } from '../../utils/scrollToPageTop'

// One colour per accounting type so a viewer learns to read the list at a
// glance: emerald for things the business owns, rose for things it owes,
// purple for owner stake, blue for income, amber for spend. Every colour
// comes from INSIGHT_PALETTE so the chart of accounts stays coherent with
// the insight tiles that already use the same palette.
const ACCOUNT_TYPE_STYLE = {
    asset:     { accent: '#10B981', label: 'Asset' },
    liability: { accent: '#E14D8B', label: 'Liability' },
    equity:    { accent: '#6418C3', label: 'Equity' },
    revenue:   { accent: '#3B82F6', label: 'Revenue' },
    expense:   { accent: '#F0A040', label: 'Expense' },
}
const FALLBACK_ACCOUNT_STYLE = { accent: '#6B7280', label: 'Account' }
const styleForAccount = (account) => ACCOUNT_TYPE_STYLE[account.account_type] ?? FALLBACK_ACCOUNT_STYLE

const AccountTypePill = ({ accent, label }) => (
    <span
        className="badge rounded-pill"
        style={{ background: `${accent}22`, color: accent, fontWeight: 600 }}
    >
        {label}
    </span>
)

const initialsFor = (name) => {
    const trimmed = String(name ?? '').trim()
    if (!trimmed) return '?'
    const parts = trimmed.split(/\s+/)
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase()
}

const AccountAvatar = ({ name, accent, size = 40 }) => (
    <div
        className="d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold"
        style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: `${accent}22`,
            color: accent,
            fontSize: size >= 40 ? 14 : 11,
        }}
        aria-hidden="true"
    >
        {initialsFor(name)}
    </div>
)

const formatAmount = (amount) =>
    `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    )
}

const ChartOfAccounts = () => {
    const userRole = useSelector((state) => state.auth.authUser?.role_slug)
    const [selectedAccount, setSelectedAccount] = useState(null)
    const [accountsPage, setAccountsPage] = useState(1)
    const [entriesPage, setEntriesPage] = useState(1)
    const entriesModalBodyRef = useRef(null)

    useEffect(() => {
        entriesModalBodyRef.current?.scrollTo({ top: 0 })
    }, [entriesPage])

    const accountsQuery = useQuery({
        queryKey: ['chart-of-accounts', accountsPage],
        queryFn: () => getChartOfAccounts(accountsPage),
        enabled: userRole === ROLE_SLUGS.EXEC_ADMIN,
        placeholderData: keepPreviousData,
        staleTime: 0,
    })

    const entriesQuery = useQuery({
        queryKey: ['chart-of-account-entries', selectedAccount?.id, entriesPage],
        queryFn: () => getChartOfAccountEntries(selectedAccount.id, entriesPage),
        enabled: !!selectedAccount,
        placeholderData: keepPreviousData,
        staleTime: 0,
    })

    const handleSelectAccount = (account) => {
        setSelectedAccount(account)
        setEntriesPage(1)
    }

    const handleCloseModal = () => {
        setSelectedAccount(null)
        setEntriesPage(1)
    }

    if (userRole !== ROLE_SLUGS.EXEC_ADMIN) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-muted mb-0">You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const accounts = accountsQuery.data?.data?.data ?? []
    const accountsTotalPages = accountsQuery.data?.data?.meta?.last_page ?? 1
    const entries = entriesQuery.data?.data?.data ?? []
    const entriesTotalPages = entriesQuery.data?.data?.meta?.last_page ?? 1

    const renderViewButton = (account) => (
        <button
            type="button"
            className="btn btn-sm btn-outline-info"
            onClick={() => handleSelectAccount(account)}
            aria-label={`View entries for ${account.name}`}
        >
            <i className="fa fa-eye me-1" aria-hidden="true" />
            View Entries
        </button>
    )

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title mb-0">Chart of Accounts</h4>
                        </div>
                        <div className="card-body p-0">
                            {accountsQuery.status === 'pending' ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : accountsQuery.error ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load accounts. Please try again.
                                </p>
                            ) : accounts.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    No accounts found.
                                </p>
                            ) : (
                                <>
                                    <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Account</th>
                                                    <th scope="col">Description</th>
                                                    <th scope="col" className="text-end">Balance</th>
                                                    <th scope="col" className="text-end">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {accounts.map((account) => {
                                                    const { accent, label } = styleForAccount(account)
                                                    return (
                                                        <tr key={account.id}>
                                                            <td className="fw-semibold" style={{ overflowWrap: 'anywhere' }}>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <AccountAvatar name={account.name} accent={accent} size={32} />
                                                                    <div>
                                                                        <div>{account.name}</div>
                                                                        <div className="mt-1"><AccountTypePill accent={accent} label={label} /></div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td style={{ maxWidth: '320px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {account.description || <span className="text-muted">-</span>}
                                                            </td>
                                                            <td className="text-end text-nowrap fw-semibold text-primary">{formatAmount(account.balance)}</td>
                                                            <td className="text-end">{renderViewButton(account)}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Account</th>
                                                    <th scope="col" className="text-end">Balance</th>
                                                    <th scope="col" className="text-end">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {accounts.map((account) => {
                                                    const { accent, label } = styleForAccount(account)
                                                    return (
                                                        <tr key={account.id}>
                                                            <td>
                                                                <div className="d-flex align-items-start gap-2">
                                                                    <AccountAvatar name={account.name} accent={accent} size={32} />
                                                                    <div style={{ minWidth: 0 }}>
                                                                        <div className="fw-semibold" style={{ overflowWrap: 'anywhere' }}>{account.name}</div>
                                                                        <div className="mt-1"><AccountTypePill accent={accent} label={label} /></div>
                                                                        {account.description && (
                                                                            <div className="small text-muted mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                                {account.description}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-end text-nowrap fw-semibold text-primary">{formatAmount(account.balance)}</td>
                                                            <td className="text-end">{renderViewButton(account)}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <ul className="list-unstyled d-block d-md-none px-3 pt-3 mb-0">
                                        {accounts.map((account) => {
                                            const { accent, label } = styleForAccount(account)
                                            return (
                                                <li key={account.id} className="mb-3">
                                                    <div
                                                        className="rounded-3 border overflow-hidden bg-white"
                                                        style={{ borderColor: 'var(--bs-border-color)' }}
                                                    >
                                                        <div style={{ height: 3, background: accent }} aria-hidden="true" />
                                                        <div className="p-3">
                                                            <div className="d-flex align-items-start gap-3">
                                                                <AccountAvatar name={account.name} accent={accent} />
                                                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                    <div className="fw-semibold text-body" style={{ overflowWrap: 'anywhere' }}>{account.name}</div>
                                                                    <div className="mt-1"><AccountTypePill accent={accent} label={label} /></div>
                                                                    {account.description && (
                                                                        <div className="small text-muted mt-2" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {account.description}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 fs-4 fw-bold text-primary text-end text-nowrap">
                                                                {formatAmount(account.balance)}
                                                            </div>
                                                            <div className="mt-3 d-grid">
                                                                {renderViewButton(account)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </>
                            )}
                            <PaginationBar
                                page={accountsPage}
                                lastPage={accountsTotalPages}
                                isLoading={accountsQuery.isFetching}
                                onPrev={() => { setAccountsPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                                onNext={() => { setAccountsPage(p => Math.min(p + 1, accountsTotalPages)); scrollToPageTop(); }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                show={!!selectedAccount}
                onHide={handleCloseModal}
                size="xl"
                centered
                scrollable
                fullscreen="sm-down"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedAccount?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" ref={entriesModalBodyRef}>
                    {entriesQuery.status === 'pending' ? (
                        <div className="d-flex justify-content-center py-4">
                            <span className="spinner-border spinner-border-sm text-primary" role="status" />
                        </div>
                    ) : entriesQuery.error ? (
                        <p className="text-center text-danger py-4 mb-0">
                            Failed to load entries. Please try again.
                        </p>
                    ) : entries.length === 0 ? (
                        <p className="text-center text-muted py-4 mb-0">
                            No entries for this account.
                        </p>
                    ) : (
                        <ul className="list-group list-group-flush">
                            {entries.map((entry) => {
                                const amount   = Number(entry.amount ?? (Number(entry.credit) > 0 ? entry.credit : entry.debit))
                                const isIn     = entry.direction === 'in'
                                const isOut    = entry.direction === 'out'
                                const signed   = isIn  ? `+${formatAmount(amount)}`
                                                 : isOut ? `-${formatAmount(amount)}`
                                                 : formatAmount(amount)
                                const colorCls = isIn ? 'text-success' : isOut ? 'text-danger' : 'text-body'
                                return (
                                    <li key={entry.id} className="list-group-item py-3">
                                        <div className="d-flex align-items-start gap-3">
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                <div className="small text-muted">
                                                    <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                    {formatDate(entry.transaction_date)}
                                                </div>
                                                {entry.description && (
                                                    <div className="mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {entry.description}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={`fw-semibold text-nowrap ${colorCls}`}>
                                                {signed}
                                            </div>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                </Modal.Body>
                {selectedAccount && entriesQuery.isSuccess && entries.length > 0 && (
                    <Modal.Footer className="flex-column align-items-stretch p-0">
                        <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light">
                            <span className="fw-semibold">Balance</span>
                            <span className="fw-semibold text-nowrap">
                                {formatAmount(selectedAccount.balance)}
                            </span>
                        </div>
                        <PaginationBar
                            page={entriesPage}
                            lastPage={entriesTotalPages}
                            isLoading={entriesQuery.isFetching}
                            onPrev={() => setEntriesPage(p => Math.max(p - 1, 1))}
                            onNext={() => setEntriesPage(p => Math.min(p + 1, entriesTotalPages))}
                        />
                    </Modal.Footer>
                )}
            </Modal>
        </Fragment>
    )
}

export default ChartOfAccounts
